// src/components/ui/AuditDetailPanel.tsx
import { useEffect, useState, type ReactNode } from 'react';
import type { AuditEntry } from '@/lib/types/audit';
import type { BackendWorkflow } from '@/lib/types/workflow';
import type { RawEmail, Draft } from '@/lib/types/booking';
import {
  getWorkflow,
  getEmail,
  getEmailDrafts,
  sendReply,
  getReplyDraftSuggestion,
  type ReplyDraftSuggestion,
} from '@/lib/services/bookings';
import { SkeletonCard } from '@/components/ui/SkeletonCard';
import { ExpandOverlay } from '@/components/ui/ExpandOverlay';
import { formatSAST } from '@/lib/utils/date';
import { statusColors } from '@/lib/statusColors';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';

export type DetailPanelEntry =
  | { kind: 'audit'; data: AuditEntry }
  | { kind: 'sent'; data: BackendWorkflow }
  | { kind: 'reply'; data: RawEmail };

export interface AuditDetailPanelProps {
  entry: DetailPanelEntry | null;
  onClose: () => void;
}

type LoadState = 'idle' | 'loading' | 'done' | 'error';

// ── Small internal sub-components ────────────────────────────────────────────

function MetaLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'block',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 9.5,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: 'rgba(var(--fg-rgb),0.3)',
        marginBottom: 3,
      }}
    >
      {children}
    </span>
  );
}

function FieldRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <MetaLabel>{label}</MetaLabel>
      <span
        style={{
          fontFamily: mono ? 'JetBrains Mono, monospace' : 'Manrope, sans-serif',
          fontSize: mono ? 11 : 13,
          color: 'rgba(var(--fg-rgb),0.7)',
          wordBreak: 'break-all',
        }}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function SectionHead({ children, badge }: { children: ReactNode; badge?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'rgba(var(--fg-rgb),0.35)',
        }}
      >
        {children}
      </span>
      {badge}
    </div>
  );
}

function CardBox({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: 'rgba(var(--fg-rgb),0.03)',
        border: '1px solid rgba(var(--fg-rgb),0.06)',
        borderRadius: 10,
        padding: '16px 18px',
      }}
    >
      {children}
    </div>
  );
}

function BodyArea({ text }: { text: string }) {
  return (
    <textarea
      readOnly
      value={text}
      style={{
        display: 'block',
        width: '100%',
        minHeight: 120,
        maxHeight: 200,
        resize: 'none',
        overflowY: 'auto',
        background: 'rgba(var(--fg-rgb),0.03)',
        border: '1px solid rgba(var(--fg-rgb),0.07)',
        borderRadius: 8,
        padding: '10px 12px',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11.5,
        lineHeight: 1.65,
        color: 'rgba(var(--fg-rgb),0.55)',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

function BodyBlock({ label, text, onExpand }: { label: string; text: string; onExpand: () => void }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'rgba(var(--fg-rgb),0.3)',
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={onExpand}
          aria-label={`Expand ${label}`}
          title="Expand"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(var(--fg-rgb),0.35)',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(var(--fg-rgb),0.7)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(var(--fg-rgb),0.35)';
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 15 }} aria-hidden>
            open_in_full
          </span>
        </button>
      </div>
      <BodyArea text={text} />
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        color: 'rgba(var(--fg-rgb),0.3)',
      }}
    >
      {children}
    </span>
  );
}

function ReadOnlyBadge() {
  return (
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: 'rgba(104, 187, 203,0.8)',
        padding: '2px 7px',
        borderRadius: 4,
        border: '1px solid rgba(104, 187, 203,0.25)',
      }}
    >
      Read Only
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AuditDetailPanel({ entry, onClose }: AuditDetailPanelProps) {
  const isOpen = entry !== null;

  const [emailState, setEmailState] = useState<LoadState>('idle');
  const [draftState, setDraftState] = useState<LoadState>('idle');
  const [rawEmail, setRawEmail] = useState<RawEmail | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [noDetail, setNoDetail] = useState(false);
  const [expandedBody, setExpandedBody] = useState<{ label: string; text: string } | null>(null);

  useEffect(() => {
    if (!entry) {
      setRawEmail(null);
      setDraft(null);
      setEmailState('idle');
      setDraftState('idle');
      setNoDetail(false);
      return;
    }

    setNoDetail(false);
    setRawEmail(null);
    setDraft(null);
    setEmailState('loading');
    setDraftState('loading');

    if (entry.kind === 'reply') {
      // Already have the full RawEmail (including body_plain) from the
      // Replies list — no fetch needed, unlike 'audit'/'sent' which only
      // carry a workflow/booking id and must look the email up.
      setRawEmail(entry.data);
      setEmailState('done');
      setDraftState('idle');
      return;
    }

    let cancelled = false;

    async function fetchAll() {
      try {
        let rawEmailId: string | null | undefined;

        if (entry!.kind === 'audit') {
          const bookingId = entry!.data.bookingId;
          if (!bookingId) {
            setNoDetail(true);
            setEmailState('idle');
            setDraftState('idle');
            return;
          }
          const wf = await getWorkflow(bookingId);
          if (cancelled) return;
          rawEmailId = (wf as Record<string, unknown>)['raw_email_id'] as string | undefined;
        } else if (entry!.kind === 'sent') {
          // 'sent' kind — workflow data is already in hand
          rawEmailId = entry!.data.raw_email_id;
        } else {
          // 'reply' kind is handled by the early return above and never
          // reaches fetchAll — this branch exists only to satisfy the
          // exhaustiveness of the discriminated union.
          return;
        }

        if (!rawEmailId) {
          setEmailState('error');
          setDraftState('error');
          setNoDetail(true);
          return;
        }

        const [emailResult, draftsResult] = await Promise.allSettled([
          getEmail(rawEmailId),
          getEmailDrafts(rawEmailId),
        ]);
        if (cancelled) return;

        if (emailResult.status === 'fulfilled') {
          setRawEmail(emailResult.value);
          setEmailState('done');
        } else {
          setEmailState('error');
        }

        if (draftsResult.status === 'fulfilled' && draftsResult.value.length > 0) {
          setDraft(draftsResult.value[0]);
          setDraftState('done');
        } else {
          setDraftState('error');
        }
      } catch {
        if (!cancelled) {
          setEmailState('error');
          setDraftState('error');
          setNoDetail(true);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.data.id]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Derived display values per kind
  const badgeStatus = entry
    ? entry.kind === 'audit'
      ? entry.data.action
      : entry.kind === 'sent'
        ? 'SENT'
        : 'REPLY'
    : undefined;
  const badgeTone = statusColors(badgeStatus);
  const badgeColor = badgeTone.color;
  const badgeLabel = badgeStatus ?? '';

  const headerActor = entry
    ? entry.kind === 'audit'
      ? entry.data.actorEmail
      : entry.kind === 'sent'
        ? (entry.data.approved_by_email ?? entry.data.approved_by_id ?? '—')
        : entry.data.sender_email
    : '';

  const headerTimestamp = entry
    ? entry.kind === 'audit'
      ? entry.data.timestamp
      : entry.kind === 'sent'
        ? (entry.data.sent_at ?? '')
        : entry.data.received_at
    : '';

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          background: 'rgba(0,0,0,0.55)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 300ms ease',
        }}
      />

      {/* Slide-over panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Detail"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          zIndex: 50,
          background: 'var(--color-surface-1)',
          borderLeft: '1px solid rgba(var(--fg-rgb),0.08)',
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 300ms ease, opacity 300ms ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid rgba(var(--fg-rgb),0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          {entry && (
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: badgeColor,
                padding: '4px 10px',
                borderRadius: 999,
                background: badgeTone.background,
                border: `1px solid ${badgeTone.border}`,
                flexShrink: 0,
              }}
            >
              {badgeLabel}
            </span>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            {entry && (
              <>
                <div
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'rgba(var(--fg-rgb),0.85)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {headerActor}
                </div>
                <div
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10.5,
                    color: 'rgba(var(--fg-rgb),0.35)',
                    marginTop: 2,
                  }}
                >
                  {headerTimestamp ? formatSAST(headerTimestamp) : '—'}
                </div>
              </>
            )}
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid rgba(var(--fg-rgb),0.08)',
              background: 'rgba(var(--fg-rgb),0.04)',
              color: 'rgba(var(--fg-rgb),0.5)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              lineHeight: 1,
              flexShrink: 0,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────── */}
        <div
          className="custom-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 32px' }}
        >
          {noDetail && (
            <div
              style={{
                marginBottom: 16,
                padding: '10px 14px',
                borderRadius: 8,
                background: 'rgba(var(--fg-rgb),0.02)',
                border: '1px solid rgba(var(--fg-rgb),0.05)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: 'rgba(var(--fg-rgb),0.3)',
              }}
            >
              Full detail unavailable for this entry
            </div>
          )}

          {/* ── Section 1: context-specific ─────────────────────── */}
          {entry?.kind === 'audit' && (
            <section style={{ marginBottom: 24 }}>
              <SectionHead>Audit Event</SectionHead>
              <CardBox>
                <div style={{ marginBottom: 12 }}>
                  <MetaLabel>Booking ID</MetaLabel>
                  <span
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 11,
                      color: 'rgba(var(--fg-rgb),0.5)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {entry.data.bookingId || '—'}
                  </span>
                </div>
                <FieldRow label="Action" value={entry.data.action} />
                {entry.data.fromStatus && entry.data.toStatus && (
                  <FieldRow label="Transition" value={`${entry.data.fromStatus} → ${entry.data.toStatus}`} mono />
                )}
                <FieldRow label="Actor" value={entry.data.actorEmail} />
                <FieldRow label="Timestamp" value={formatSAST(entry.data.timestamp)} />
                <div>
                  <MetaLabel>Notes</MetaLabel>
                  <span
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: 13,
                      color: 'rgba(var(--fg-rgb),0.7)',
                    }}
                  >
                    {entry.data.notes || '—'}
                  </span>
                </div>
              </CardBox>
            </section>
          )}

          {entry?.kind === 'sent' && (
            <section style={{ marginBottom: 24 }}>
              <SectionHead>Workflow</SectionHead>
              <CardBox>
                <div style={{ marginBottom: 12 }}>
                  <MetaLabel>Workflow ID</MetaLabel>
                  <span
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 11,
                      color: 'rgba(var(--fg-rgb),0.5)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {entry.data.id}
                  </span>
                </div>
                <FieldRow label="Status" value={entry.data.status} mono />
                <FieldRow label="Approved By" value={entry.data.approved_by_email ?? entry.data.approved_by_id ?? '—'} />
                <FieldRow label="Approved At" value={entry.data.approved_at ? formatSAST(entry.data.approved_at) : '—'} />
                <FieldRow label="Sent At" value={entry.data.sent_at ? formatSAST(entry.data.sent_at) : '—'} />
                {entry.data.status_reason && (
                  <FieldRow label="Reason" value={entry.data.status_reason} />
                )}
              </CardBox>
            </section>
          )}

          {/* Section 2 — Original Email / Client Reply */}
          <section style={{ marginBottom: 24 }}>
            <SectionHead>{entry?.kind === 'reply' ? 'Client Reply' : 'Original Email'}</SectionHead>
            <CardBox>
              {emailState === 'loading' && (
                <div className="space-y-3">
                  <SkeletonCard height={12} width="60%" />
                  <SkeletonCard height={12} width="80%" />
                  <SkeletonCard height={12} width="45%" />
                  <SkeletonCard height={80} />
                </div>
              )}
              {emailState === 'done' && rawEmail && (
                <>
                  <FieldRow label="From" value={rawEmail.sender_email} />
                  <FieldRow label="Subject" value={rawEmail.subject} />
                  <FieldRow label="Received" value={formatSAST(rawEmail.received_at)} />
                  <BodyBlock
                    label="Body"
                    text={rawEmail.body_plain ?? ''}
                    onExpand={() =>
                      setExpandedBody({
                        label: entry?.kind === 'reply' ? 'Client Reply' : 'Original Email',
                        text: rawEmail.body_plain ?? '',
                      })
                    }
                  />
                </>
              )}
              {(emailState === 'error' || emailState === 'idle') && (
                <Muted>Original email unavailable</Muted>
              )}
            </CardBox>
          </section>

          {/* Section 3 — Draft (audit/sent only) */}
          {entry?.kind !== 'reply' && (
            <section>
              <SectionHead badge={<ReadOnlyBadge />}>
                {entry?.kind === 'sent' ? 'Sent Draft' : 'AI Draft Response'}
              </SectionHead>
              <CardBox>
                {draftState === 'loading' && (
                  <div className="space-y-3">
                    <SkeletonCard height={12} width="70%" />
                    <SkeletonCard height={80} />
                  </div>
                )}
                {draftState === 'done' && draft && (
                  <>
                    <FieldRow label="Subject" value={draft.subject} />
                    <BodyBlock
                      label="Body"
                      text={draft.body_plain}
                      onExpand={() =>
                        setExpandedBody({
                          label: entry?.kind === 'sent' ? 'Sent Draft' : 'AI Draft Response',
                          text: draft.body_plain,
                        })
                      }
                    />
                  </>
                )}
                {(draftState === 'error' || draftState === 'idle') && (
                  <Muted>
                    {entry?.kind === 'sent'
                      ? 'No sent draft associated with this workflow'
                      : 'No draft associated with this event'}
                  </Muted>
                )}
              </CardBox>
            </section>
          )}

          {/* Section 3 (reply kind) — compose and send a direct reply */}
          {entry?.kind === 'reply' && rawEmail && (
            <ReplyComposer rawEmail={rawEmail} onSent={onClose} />
          )}
        </div>
      </div>

      <ExpandOverlay
        open={expandedBody !== null}
        title={expandedBody?.label ?? ''}
        subtitle="Body"
        onClose={() => setExpandedBody(null)}
        contentFill
      >
        <textarea
          readOnly
          value={expandedBody?.text ?? ''}
          style={{
            flex: 1,
            width: '100%',
            resize: 'none',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 13,
            lineHeight: 1.7,
            color: 'rgba(var(--fg-rgb),0.75)',
            boxSizing: 'border-box',
          }}
        />
      </ExpandOverlay>
    </>
  );
}

// ── Reply composer (kind: 'reply' only) ──────────────────────────────────────
// Direct send, no approval step — see app.api.v1.replies
// (glow-ai-workflow-service) and [[finding-reply-reingested-as-new-booking]].

function ReplyComposer({ rawEmail, onSent }: { rawEmail: RawEmail; onSent: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Set only when the last-generated suggestion carried real pricing (the
  // reply requested a change draft-service could re-price) — null clears
  // the summary below, since a manually-edited or unpriced draft shouldn't
  // still show stale figures next to it. See
  // [[feature-scope-ai-priced-reply-quote]].
  const [pricing, setPricing] = useState<ReplyDraftSuggestion | null>(null);
  const [expanded, setExpanded] = useState(false);

  async function handleGenerateDraft() {
    if (generating || sending) return;
    if (body.trim() && !window.confirm('This will replace your current draft with an AI-generated one. Continue?')) {
      return;
    }
    setGenerating(true);
    try {
      const suggestion = await getReplyDraftSuggestion(rawEmail.id);
      setBody(suggestion.suggested_body_text);
      setPricing(suggestion.total_price !== null ? suggestion : null);
      toast.success(
        suggestion.total_price !== null
          ? 'AI draft generated with updated pricing — review before sending'
          : 'AI draft generated — review before sending'
      );
    } catch (err: unknown) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 404) {
        toast.error('No original booking found for this thread — cannot generate an AI draft');
      } else if (status === 422) {
        toast.error('This email is not a recognised thread reply — cannot generate an AI draft');
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to generate AI draft');
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleSend() {
    if (!body.trim()) return;
    setSending(true);
    try {
      await sendReply(rawEmail.id, {
        actor_id: user?.id ?? 'unknown',
        body_text: body,
      });
      toast.success('Reply sent');
      setBody('');
      setPricing(null);
      onSent();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <SectionHead>Send Reply</SectionHead>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Expand reply editor"
          title="Expand"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(var(--fg-rgb),0.35)',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(var(--fg-rgb),0.7)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(var(--fg-rgb),0.35)';
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 15 }} aria-hidden>
            open_in_full
          </span>
        </button>
      </div>
      <CardBox>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <MetaLabel>To</MetaLabel>
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: 'rgba(var(--fg-rgb),0.5)',
              }}
            >
              {rawEmail.sender_email}
            </span>
          </div>
          <button
            className="btn-ghost"
            onClick={handleGenerateDraft}
            disabled={generating || sending}
            style={{
              padding: '7px 14px',
              fontSize: 12,
              whiteSpace: 'nowrap',
              opacity: generating || sending ? 0.5 : 1,
            }}
          >
            {generating ? 'Generating…' : 'Generate AI Draft'}
          </button>
        </div>
        {pricing && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(var(--fg-rgb),0.03)',
              border: '1px solid rgba(var(--fg-rgb),0.1)',
            }}
          >
            <MetaLabel>Updated Quote (as generated)</MetaLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {(pricing.pricing_line_items ?? []).map((line, idx) => (
                <div
                  key={`${line.name}-${idx}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 12,
                    fontFamily: 'Manrope, sans-serif',
                    color: line.matched ? 'rgba(var(--fg-rgb),0.75)' : 'var(--error, #ffb4ab)',
                  }}
                >
                  <span>
                    {line.name} × {line.quantity}
                    {!line.matched && ' — unmatched, price to confirm manually'}
                  </span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>
                    {line.matched ? `R${line.line_total.toFixed(2)}` : '—'}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 13,
                  fontWeight: 600,
                  marginTop: 4,
                  paddingTop: 6,
                  borderTop: '1px solid rgba(var(--fg-rgb),0.1)',
                  color: 'rgba(var(--fg-rgb),0.9)',
                }}
              >
                <span>Total ({pricing.price_currency ?? 'ZAR'})</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  R{(pricing.total_price ?? 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your reply… or generate an AI draft above"
          disabled={sending || generating}
          style={{
            display: 'block',
            width: '100%',
            minHeight: 140,
            resize: 'vertical',
            background: 'rgba(var(--fg-rgb),0.03)',
            border: '1px solid rgba(var(--fg-rgb),0.1)',
            borderRadius: 8,
            padding: '10px 12px',
            fontFamily: 'Manrope, sans-serif',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'rgba(var(--fg-rgb),0.85)',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 12,
            opacity: generating ? 0.6 : 1,
          }}
        />
        <button
          className="btn-primary"
          onClick={handleSend}
          disabled={sending || generating || !body.trim()}
          style={{ padding: '8px 16px', fontSize: 12.5, opacity: sending || generating || !body.trim() ? 0.5 : 1 }}
        >
          {sending ? 'Sending…' : 'Send Reply'}
        </button>
      </CardBox>

      <ExpandOverlay
        open={expanded}
        title="Send Reply"
        subtitle={`To ${rawEmail.sender_email}`}
        onClose={() => setExpanded(false)}
        contentFill
      >
        <textarea
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your reply… or generate an AI draft above"
          disabled={sending || generating}
          style={{
            flex: 1,
            width: '100%',
            resize: 'none',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: 'Manrope, sans-serif',
            fontSize: 14,
            lineHeight: 1.7,
            color: 'rgba(var(--fg-rgb),0.85)',
            boxSizing: 'border-box',
            opacity: generating ? 0.6 : 1,
          }}
        />
      </ExpandOverlay>
    </section>
  );
}
